/**
 * Browser extensions (Grammarly, Google Translate, TransOver) swap text nodes
 * React is tracking, so a later React commit calls removeChild/insertBefore on
 * a node that is no longer where React last saw it, throwing and unmounting the
 * whole app. Tolerating a mismatch here (log + no-op) is strictly safer than the
 * crash: the extension already mutated the DOM, so skipping the now-invalid
 * operation leaves the page in the same visual state the extension produced.
 */
export function installDomGuard(): void {
  if (typeof Node !== 'function' || !Node.prototype) return;

  const originalRemoveChild = Node.prototype.removeChild;
  Node.prototype.removeChild = function <T extends Node>(this: Node, child: T): T {
    if (child.parentNode !== this) {
      if (console) console.error('[domGuard] removeChild: child is not a child of this node', child, this);
      return child;
    }
    return originalRemoveChild.call(this, child) as T;
  };

  const originalInsertBefore = Node.prototype.insertBefore;
  Node.prototype.insertBefore = function <T extends Node>(this: Node, newNode: T, referenceNode: Node | null): T {
    if (referenceNode && referenceNode.parentNode !== this) {
      if (console) console.error('[domGuard] insertBefore: reference node is not a child of this node', referenceNode, this);
      return newNode;
    }
    return originalInsertBefore.call(this, newNode, referenceNode) as T;
  };
}
